import {
  SETTINGS_TAB_MODULES,
  effectivePermissionSetHas,
  type SettingsTabId,
} from '@utazzo/shared';

/**
 * Which Settings tabs a caller may see, and which they may save.
 *
 * 2026-08-04. Tab visibility used to be `role === 'ADMIN'` for Tax & Pricing
 * and Scripts & tags, and "everyone with the page gate" for the other two -
 * so those two surfaces could not be delegated at all, and the first two could
 * not be withheld. Each tab now has its own permission module and this is the
 * single place that maps a permission set onto the tab list.
 *
 * Pure and exported so the rules are unit-testable without rendering the page.
 */

export const SETTINGS_TAB_ORDER: readonly SettingsTabId[] = [
  'website',
  'branding',
  'tax',
  'scripts',
  'company',
];

/** `sys.settings_<tab>.read` for a tab. */
export const settingsTabReadCode = (tab: SettingsTabId): string =>
  `${SETTINGS_TAB_MODULES[tab]}.read`;

/** `sys.settings_<tab>.write` for a tab. */
export const settingsTabWriteCode = (tab: SettingsTabId): string =>
  `${SETTINGS_TAB_MODULES[tab]}.write`;

/**
 * Tabs the caller can open, in display order.
 *
 * `undefined` permissions means the /me round-trip hasn't landed yet - return
 * nothing rather than guessing, so a tab never flashes into view and
 * disappears. `null` (loaded, no grants) is genuinely empty.
 */
export const visibleSettingsTabs = (
  effectivePermissions: Set<string> | null | undefined,
): SettingsTabId[] => {
  if (!effectivePermissions) return [];
  return SETTINGS_TAB_ORDER.filter((tab) =>
    effectivePermissionSetHas(effectivePermissions, settingsTabReadCode(tab)),
  );
};

/** Whether the caller may save changes on a tab. */
export const canWriteSettingsTab = (
  effectivePermissions: Set<string> | null | undefined,
  tab: SettingsTabId,
): boolean =>
  effectivePermissions
    ? effectivePermissionSetHas(effectivePermissions, settingsTabWriteCode(tab))
    : false;
